import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Badge, Popover, Flex, Typography } from "antd";
import {
  BellIcon,
  BellRingingIcon,
  TrendUpIcon,
  EyeIcon,
  FireIcon,
  ChatTeardropTextIcon,
  TargetIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import styled from "styled-components";
import { Button } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";
import { NOTIFICATIONS_EVENT_TYPE } from "store/websocketStore";
import { analyticsTheme } from "../../components/analytics/analyticsTheme";
import {
  LiveNotification,
  LiveNotificationType,
} from "../hooks/useLiveNotifications";

export type LiveNotificationsBellProps = {
  notifications: LiveNotification[];
  unseenCount: number;
  /** Experience titles by project id; a missing title is simply not shown. */
  projectTitleById: Record<string, string>;
  /** Opens the notification's target page. */
  onOpen: (notification: LiveNotification) => void;
  /** Marks every notification as read (clears the badge and the dots). */
  onMarkAllRead: () => void;
};

const typeIcon: Record<LiveNotificationType, typeof TrendUpIcon> = {
  [NOTIFICATIONS_EVENT_TYPE.NewMaxConcurrentViewers]: TrendUpIcon,
  [NOTIFICATIONS_EVENT_TYPE.ViewMilestoneReached]: EyeIcon,
  [NOTIFICATIONS_EVENT_TYPE.ProjectTrending]: FireIcon,
  [NOTIFICATIONS_EVENT_TYPE.ChatbotMessageAnswered]: ChatTeardropTextIcon,
  [NOTIFICATIONS_EVENT_TYPE.QuizSubmitted]: TargetIcon,
};

export const LiveNotificationsBell = ({
  notifications,
  unseenCount,
  projectTitleById,
  onOpen,
  onMarkAllRead,
}: LiveNotificationsBellProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [open, setOpen] = useState(false);
  const hasUnseen = unseenCount > 0;
  const Icon = hasUnseen ? BellRingingIcon : BellIcon;
  const label = hasUnseen
    ? t("media.analytics.notifications.bellLabel", { count: unseenCount })
    : t("media.analytics.notifications.bellLabelIdle");

  const messageFor = (n: LiveNotification): string => {
    switch (n.type) {
      case NOTIFICATIONS_EVENT_TYPE.NewMaxConcurrentViewers:
        return t("media.analytics.peakToast", { count: n.count });
      case NOTIFICATIONS_EVENT_TYPE.ViewMilestoneReached:
        return t("media.analytics.milestoneToast", { count: n.count });
      case NOTIFICATIONS_EVENT_TYPE.ProjectTrending:
        return t("media.analytics.notifications.trending");
      case NOTIFICATIONS_EVENT_TYPE.ChatbotMessageAnswered:
        return t("media.analytics.notifications.chatbotQuestion");
      case NOTIFICATIONS_EVENT_TYPE.QuizSubmitted:
        return typeof n.count === "number"
          ? t("media.analytics.notifications.quizCompletedScore", {
              score: Math.round(n.count),
            })
          : t("media.analytics.notifications.quizCompleted");
    }
  };

  const handleOpen = (notification: LiveNotification) => {
    onOpen(notification);
    setOpen(false);
  };

  const content = (
    <Flex
      vertical
      gap={4}
      style={{ width: 280, maxHeight: 360, overflowY: "auto" }}
    >
      <Flex justify="space-between" align="center" gap={12}>
        <Typography.Text strong>
          {t("media.analytics.notifications.title")}
        </Typography.Text>
        {hasUnseen && (
          <Button
            variant="tertiary"
            size="s"
            onClick={onMarkAllRead}
            style={{ color: theme.colorPrimary, boxShadow: "none" }}
          >
            {t("media.analytics.notifications.markAllRead")}
          </Button>
        )}
      </Flex>
      {notifications.length === 0 ? (
        <Typography.Text type="secondary" style={{ textAlign: "center" }}>
          {t("media.analytics.notifications.empty")}
        </Typography.Text>
      ) : (
        notifications.map((notification) => {
          const ItemIcon = typeIcon[notification.type];
          const title = projectTitleById[notification.projectId];
          return (
            <StyledItem
              key={notification.id}
              type="button"
              onClick={() => handleOpen(notification)}
              $focusColor={theme.colorPrimary}
            >
              <Flex align="center" gap={10}>
                <ItemIcon size={18} color={theme.colorPrimary} />
                <Flex vertical gap={0} style={{ minWidth: 0 }}>
                  {title ? (
                    <Typography.Text strong ellipsis>
                      {title}
                    </Typography.Text>
                  ) : null}
                  <Typography.Text ellipsis>
                    {messageFor(notification)}
                  </Typography.Text>
                  <Flex align="center" gap={6}>
                    {!notification.seen && (
                      <Badge size="small" status="processing" color={"green"} />
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(notification.timestamp).fromNow()}
                    </Typography.Text>
                  </Flex>
                </Flex>
              </Flex>
            </StyledItem>
          );
        })
      )}
    </Flex>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      <Badge count={unseenCount} offset={[-5, 5]} size="small">
        <Button
          variant="outlined"
          shape="circle"
          aria-label={label}
          style={{
            borderColor: analyticsTheme.cardBorder,
            color: analyticsTheme.textLabel,
          }}
        >
          <Icon size={15} weight={hasUnseen ? "fill" : "regular"} />
        </Button>
      </Badge>
    </Popover>
  );
};

// ─── Styled components ───

const StyledItem = styled.button<{ $focusColor: string }>`
  display: block;
  width: 100%;
  padding: 8px 4px;
  border: none;
  border-top: 1px solid ${analyticsTheme.barTrack};
  background: none;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ $focusColor }) => $focusColor};
    outline-offset: -2px;
  }
`;
